import React from 'react'
import { FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import { useTranslation } from 'react-i18next'
import i18n from '~/components/i18n'
import { FixedPricePrefixType } from '~/api/services/dashboard'
import ActivityTable from './ActivityTable'

interface TradingActivitiesProps {
  statusOptions: any[]
}

const getFAndPTableColumnHeaders = (priceLabel) => [
  {
    name: i18n.t('Trading slot ID'),
    key: 'tradingSlotUniqueId'
  },
  {
    name: i18n.t('Offer ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Offer date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Trading month'),
    key: 'date',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Price {{priceLabel}}', { priceLabel }),
    key: 'price',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Offered amount (in kg)'),
    key: 'amount',
    type: 'number'
  },
  {
    name: i18n.t('Confirmed amount (in kg)'),
    key: 'confirmedAmount',
    type: 'number'
  },
  {
    name: i18n.t('Actions'),
    type: 'delete-offer',
    key: 'delete'
  }
]

const getTableFixedPColumnHeaders = (priceLabel, amountUnit) => [
  {
    name: i18n.t('Trading slot ID'),
    key: 'tradingSlotUniqueId'
  },
  {
    name: i18n.t('Offer ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Offer date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Trading month'),
    key: 'date',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Price {{priceLabel}}', { priceLabel }),
    key: 'price',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Offered amount (in {{amountUnit}})', { amountUnit }),
    key: 'amount',
    type: 'number'
  },
  {
    name: i18n.t('Confirmed amount (in {{amountUnit}})', { amountUnit }),
    key: 'confirmedAmount',
    type: 'number'
  },
  {
    name: i18n.t('Actions'),
    type: 'delete-offer',
    key: 'delete'
  }
]

const getTableStockEColumnHeaders = () => [
  {
    name: i18n.t('Trading slot ID'),
    key: 'tradingSlotUniqueId'
  },
  {
    name: i18n.t('Offer ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Offer date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Trading month'),
    key: 'date',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('BLE price (in € Ct/kg)'),
    key: 'price',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Offered amount (in kg)'),
    key: 'amount',
    type: 'number'
  },
  {
    name: i18n.t('Hedging amount (in kg)'),
    key: 'confirmedAmount',
    type: 'number'
  },
  {
    name: i18n.t('Actions'),
    type: 'delete-offer',
    key: 'delete'
  }
]

// Define the type for the trading activities
interface TradingActivity {
  tableColumnHeaders: any[];
  title: string;
}

const TradingActivities: React.FC<TradingActivitiesProps> = (props) => {
  const { t } = useTranslation()
  const { systemData: { modules, amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceFixedLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`

  const tradingActivities = modules.reduce((prevValue, currentValue) => {
    if (currentValue.slug === MILK_FIXED_SLUG) {
      prevValue[FixedPricePrefixType.FixedPrice] = {
        tableColumnHeaders: getTableFixedPColumnHeaders(priceFixedLabel, amountUnit),
        title: t('Milk fixed price')
      };
    } else if (currentValue.slug === FAT_PROTEIN_SLUG) {
      prevValue[FixedPricePrefixType.FatProtein] = {
        tableColumnHeaders: getFAndPTableColumnHeaders('(€ Ct/kg)'),
        title: t('Fat & protein')
      };
    } else if (currentValue.slug === MILK_STOCK_EXCHANGE) {
      prevValue[FixedPricePrefixType.StockExchange] = {
        tableColumnHeaders: getTableStockEColumnHeaders(),
        title: t('Stock exchange')
      };
    }
    return prevValue;
  }, {} as Record<FixedPricePrefixType, TradingActivity>);

  return <div>
    {Object.values(tradingActivities).map((tradingActivitie, index) => {
      const prefix = Object.keys(tradingActivities)[index] as FixedPricePrefixType
      return <ActivityTable
        key={prefix}
        prefix={prefix}
        tableColumnHeaders={tradingActivitie?.tableColumnHeaders || []}
        title={tradingActivitie.title || ''}
        statusOptions={props.statusOptions}
      />
    })}
  </div>
}
export default TradingActivities
